import styled, { css, keyframes } from 'styled-components';
import { ReactNode, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { Brawler as BrawlerType, PickMode, TeamColorsValues } from '../common.type';
import { LayoutContext } from '../context/LayoutContext';
import { Displays, DisplaysValues } from '../type';
import { ThemeType } from '../theme';

const borderKeyframes = (props: { theme: ThemeType }) => keyframes`
    0% {
        border-color: ${props.theme.color.white};
    }
    50% {
        border-color: ${props.theme.color.yellow};
    }
    100% {
        border-color: ${props.theme.color.white};
    }
`;

const Container = styled.div`
  position: relative;
`;

const Sizes = {
  XS: { width: 10, height: 8.88 },
  S: { width: 12, height: 10.66 },
  M: { width: 20, height: 14.3575 },
  L: { width: 27, height: 24 },
};
type SizesValues = keyof typeof Sizes;

const Button = styled.button<{
  id: number | undefined;
  $border?: boolean | undefined;
  color?: TeamColorsValues | undefined;
  $ban?: boolean | undefined;
  $locked?: boolean | undefined;
  $selected?: boolean | undefined;
  $displayMode: DisplaysValues;
  size: SizesValues;
}>`
  display: flex;
  flex-direction: column-reverse;
  cursor: ${(props) => (props.$locked ? `default` : `pointer`)};
  width: ${(props) => `${Sizes[props.size].width.toString()}vmin`};
  height: ${(props) => `${Sizes[props.size].height.toString()}vmin`};
  padding: 0;
  background: ${(props) =>
    props.id
      ? ` url(${process.env.PUBLIC_URL}/images/brawlers/${props.id.toString()}.webp)`
      : 'rgba(0,0,0,0.2)'};
  background-size: cover;
  background-repeat: no-repeat;
  border-radius: 0.5vmin;
  border: 0.4vmin solid #fff;
  ${(props) =>
    props.$border &&
    `border: 0.7vmin solid ${props.$locked ? props.theme.color.green : props.$ban ? 'transparent' : props.theme.brawler.border};`}
  ${(props) =>
    !props.id &&
    `
    flex:1;
    justify-content:center;
    align-items:center;
    `}
  &:disabled {
    ${(props) =>
      props.color && `border-color: ${props.theme.team[props.color]};`}
    justify-content: center;
  }

  ${(props) =>
    props.$selected &&
    css`
      animation: ${borderKeyframes} 1.5s infinite linear;
    `}
`;
const Name = styled.div<{
  $disabled?: boolean | undefined;
  color?: TeamColorsValues | undefined;
  $displayMode: DisplaysValues;
}>`
  background: ${(props) =>
    props.$disabled && props.color
      ? props.theme.team[props.color]
      : 'rgba(0, 0, 0, 0.4)'};
  font-size: ${(props) =>
    props.$displayMode === Displays.LANDSCAPE ? '3vmin' : '5vmin'};
  color: #fff;
  text-shadow: 0 0.35vmin 0 #000;
  -webkit-text-stroke: 0.025vmin #000;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: center;
  overflow: hidden;
  width: 100%;
  padding: 0 0.5vmin;
`;
const Unknown = styled.div<{ $ban: boolean }>`
  color: rgba(0, 0, 0, 0.3);
  font-size: ${(props) => (props.$ban ? '5vmin' : '12vmin')};
`;

type BrawlerProps = {
  mode: 'draft' | 'ban' | 'list';
  color?: TeamColorsValues | undefined;
  brawler?: BrawlerType | undefined;
  border?: boolean;
  locked?: boolean | undefined;
  selected?: boolean | undefined;
  disabled?: PickMode | undefined;
  onClick?: () => void;
  children?: ReactNode;
};

export const Brawler = ({
  mode,
  color,
  brawler,
  border,
  locked,
  selected,
  disabled,
  onClick,
  children,
}: BrawlerProps) => {
  const { displayMode } = useContext(LayoutContext);

  return (
    <Container>
      <Button
        onClick={() => {
          if (locked) return;
          if (onClick) onClick();
        }}
        id={brawler?.id}
        $displayMode={displayMode}
        color={color}
        $border={border}
        $ban={mode === 'ban'}
        $locked={locked}
        $selected={selected}
        disabled={!!disabled}
        size={
          mode === 'ban'
            ? 'XS'
            : mode === 'list'
              ? 'S'
              : displayMode === Displays.LANDSCAPE
                ? 'M'
                : 'L'
        }
      >
        {mode === 'draft' && brawler && (
          <Name $disabled={!!disabled} color={color} $displayMode={displayMode}>
            {disabled ? (
              <FormattedMessage id={disabled === 'brawler' ? 'pick' : 'ban'} />
            ) : (
              brawler.name
            )}
          </Name>
        )}
        {!brawler && <Unknown $ban={mode === 'ban'}>?</Unknown>}
      </Button>
      {children}
    </Container>
  );
};
